import { FC, useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Alert,
} from "@mui/material";
import { makeSxStyles } from "ui";
import AddressBoxComponent from "../../../pages/dashboard/address-box-component/address-box-component";
import { MapMarker } from "../../../components/map/map.types";
import useNotificationRecipientsStore from "../../../state/notification-recipients-store";

export interface NotificationRecipientsSidebarProps {
  selectedAddresses: MapMarker[];
  removeSelected: (id: string) => void;
  onResetSelection: () => void;
  invalidCustomers: number;
}

const NotificationRecipientsSidebar: FC<NotificationRecipientsSidebarProps> = ({
  selectedAddresses,
  removeSelected,
  onResetSelection,
  invalidCustomers,
}) => {
  const styles = useStyles();

  const {
    addresses,
    setSelectedAddress,
    setShowFlatRateOnly,
    setShowOnDemandOnly,
    setShowSnowReporters,
    visibleRegions,
    toggleRegionVisibility,
    clearMapCentering,
    setMapCentering,
    hasManualSelection,
    resetHasManualSelection,
    showFlatRateOnly,
    showOnDemandOnly,
    showSnowReporters,
  } = useNotificationRecipientsStore();

  // Wrapper function to handle address removal with Quick Select sync
  const handleRemoveSelected = (id: string) => {
    // Find the address being removed to check its region
    const addressBeingRemoved = selectedAddresses.find(
      (addr) => addr.id === id,
    );

    // If the removed address belongs to a selected region, clear that region from dropdown
    if (addressBeingRemoved?.region && selectedRegionIds.length > 0) {
      const removedRegionId = addressBeingRemoved.region.id;

      // Remove the region from selection if it's in the dropdown
      if (selectedRegionIds.includes(removedRegionId)) {
        const updatedRegionIds = selectedRegionIds.filter(
          (regionId) =>
            regionId !== removedRegionId && regionId !== "__ALL_REGIONS__",
        );
        setSelectedRegionIds(updatedRegionIds);
      }
    }

    // Update tracking when removing addresses that were added by Quick Select
    setAddressesAddedByQuickSelect((prev) => {
      const updatedCustomerGroups = new Set(prev.customerGroups);
      const updatedRegions = new Set(prev.regions);

      updatedCustomerGroups.delete(id);
      updatedRegions.delete(id);

      return {
        customerGroups: updatedCustomerGroups,
        regions: updatedRegions,
      };
    });

    // Check if we need to reset Quick Select dropdowns
    checkAndResetQuickSelectDropdowns();

    // Remove the address
    removeSelected(id);
  };

  const handleToggleAddress = (id: string) => {
    const address = addresses.find((addr) => addr.id === id);
    if (!address) {
      handleRemoveSelected(id);
      return;
    }

    const isCurrentlySelected = selectedAddresses.some(
      (addr) => addr.id === id,
    );

    if (isCurrentlySelected) {
      handleRemoveSelected(id);
      // Clear map centering when removing addresses to prevent unwanted zooming
      clearMapCentering();
    } else {
      const updatedSelection = [
        ...selectedAddresses,
        { ...address, selected: true },
      ];
      setSelectedAddress(updatedSelection, false, false); // isInitialLoad = false since this is a user interaction
      // Clear map centering when adding addresses to prevent unwanted zooming
      clearMapCentering();
    }
  };

  // State for selected values in dropdowns
  const [selectedCustomerGroups, setSelectedCustomerGroups] = useState<
    string[]
  >([]);
  const [selectedRegionIds, setSelectedRegionIds] = useState<string[]>([]);

  // Track which addresses were added by quick select (to enable proper removal)
  const [_, setAddressesAddedByQuickSelect] = useState<{
    customerGroups: Set<string>; // address IDs added by customer group selection
    regions: Set<string>; // address IDs added by region selection
  }>({
    customerGroups: new Set(),
    regions: new Set(),
  });

  // Clear Quick select dropdown values when manual selections are made
  useEffect(() => {
    if (hasManualSelection) {
      // Clear the dropdown values when user makes manual selections (lasso, click, etc.)
      setSelectedCustomerGroups([]);
      setSelectedRegionIds([]);

      // Clear the tracking of addresses added by quick select
      setAddressesAddedByQuickSelect({
        customerGroups: new Set(),
        regions: new Set(),
      });

      // Reset the manual selection flag after clearing
      resetHasManualSelection();
    }
  }, [hasManualSelection, resetHasManualSelection]);

  // Clear Quick select dropdown values only when region visibility filters change
  // Customer type filters (Flat Rate, On Demand, etc.) should not clear region selection

  // Track previous region visibility state to detect changes
  const [previousVisibleRegions, setPreviousVisibleRegions] = useState(
    new Set(visibleRegions),
  );

  useEffect(() => {
    const currentVisibleRegions = new Set(visibleRegions);

    // Check if region visibility has changed
    const hasRegionVisibilityChanged =
      previousVisibleRegions.size !== currentVisibleRegions.size ||
      !Array.from(previousVisibleRegions).every((id) =>
        currentVisibleRegions.has(id),
      );

    if (hasRegionVisibilityChanged) {
      // Only clear region selection when region visibility filters change
      // Don't touch customer group selection as it's independent
      setSelectedRegionIds([]);

      // Clear the tracking of addresses added by quick select for regions
      setAddressesAddedByQuickSelect((prev) => ({
        customerGroups: prev.customerGroups,
        regions: new Set(),
      }));

      // Update the previous state
      setPreviousVisibleRegions(currentVisibleRegions);
    }
  }, [visibleRegions, previousVisibleRegions]);

  // Get unique regions from addresses
  const availableRegions = useMemo(() => {
    const regions = new Map();
    addresses.forEach((address) => {
      if (address.region) {
        regions.set(address.region.id, address.region);
      }
    });
    return Array.from(regions.values());
  }, [addresses]);

  // Customer group options with indentation
  const customerGroupOptions = [
    { value: "all", label: "All Customers" },
    { value: "flatRate", label: "  Flat Rate" }, // Indented
    { value: "onDemand", label: "  On Demand" }, // Indented
    { value: "snowReporters", label: "  Snow Reporters" }, // Indented
  ];

  // Region options with "All Regions" first
  const regionOptions = useMemo(() => {
    const options = [{ value: "__ALL_REGIONS__", label: "All Regions" }];
    availableRegions.forEach((region) => {
      options.push({ value: region.id, label: region.name });
    });
    return options;
  }, [availableRegions]);

  // Handle customer group selection with proper "All" logic
  const handleCustomerGroupChange = (values: string[]) => {
    let updatedValues = [...values];

    // SAME LOGIC AS REGIONS:
    // 1. If user clicked "All Customers" -> select all individual customer types too
    // 2. If user clicked "All Customers" to deselect -> clear everything
    // 3. Otherwise -> just use what user selected

    if (
      values.includes("all") &&
      values.length > selectedCustomerGroups.length
    ) {
      // User actually clicked "All Customers" (length increased)
      updatedValues = ["all", "flatRate", "onDemand", "snowReporters"];
    } else if (
      !values.includes("all") &&
      selectedCustomerGroups.includes("all")
    ) {
      // User had "All Customers" selected and clicked it to deselect everything
      updatedValues = [];
    } else {
      // User deselected individual customer types or made normal selections
      // Just use exactly what they selected - don't add anything
      updatedValues = values.filter((v) => v !== "all");

      // Only auto-add "All Customers" if they manually selected every single individual type
      const allIndividualCustomersSelected =
        values.length > 0 &&
        ["flatRate", "onDemand", "snowReporters"].every((type) =>
          values.includes(type),
        );

      if (allIndividualCustomersSelected) {
        updatedValues = ["all", ...values];
      }
    }

    setSelectedCustomerGroups(updatedValues);

    // Only clear map centering if no regions are selected
    // If regions are selected, preserve the region-based centering
    if (selectedRegionIds.length === 0) {
      clearMapCentering();
    }

    // Auto-enable corresponding filters when selecting customer groups
    if (updatedValues.includes("flatRate") || updatedValues.includes("all")) {
      setShowFlatRateOnly(true);
    }
    if (updatedValues.includes("onDemand") || updatedValues.includes("all")) {
      setShowOnDemandOnly(true);
    }
    if (
      updatedValues.includes("snowReporters") ||
      updatedValues.includes("all")
    ) {
      setShowSnowReporters(true);
    }

    // Apply smart selection logic: Customer Types AND Regions with proper tracking
    applySmartSelection(updatedValues, selectedRegionIds);
  };

  // Handle region selection with proper "All" logic
  const handleRegionChange = (values: string[]) => {
    let updatedValues = [...values];

    // SIMPLE LOGIC:
    // 1. If user clicked "All Regions" -> select all individual regions too
    // 2. If user has all individual regions selected -> add "All Regions"
    // 3. Otherwise -> just use what user selected

    if (
      values.includes("__ALL_REGIONS__") &&
      values.length > selectedRegionIds.length
    ) {
      // User actually clicked "All Regions" (length increased)
      const allRegionIds = availableRegions.map((region) => region.id);
      updatedValues = ["__ALL_REGIONS__", ...allRegionIds];
    } else if (
      !values.includes("__ALL_REGIONS__") &&
      selectedRegionIds.includes("__ALL_REGIONS__")
    ) {
      // User had "All Regions" selected and clicked it to deselect everything
      updatedValues = [];
    } else {
      // User deselected individual regions or made normal selections
      // Just use exactly what they selected - don't add anything
      updatedValues = values.filter((v) => v !== "__ALL_REGIONS__");

      // Only auto-add "All Regions" if they manually selected every single individual region
      const allIndividualRegionsSelected =
        values.length > 0 &&
        availableRegions.every((region) => values.includes(region.id));

      if (allIndividualRegionsSelected) {
        updatedValues = ["__ALL_REGIONS__", ...values];
      }
    }

    setSelectedRegionIds(updatedValues);

    // Auto-enable region visibility filters when selecting regions
    const regionIdsToEnable = updatedValues.filter(
      (v) => v !== "__ALL_REGIONS__",
    );
    regionIdsToEnable.forEach((regionId) => {
      if (!visibleRegions.has(regionId)) {
        toggleRegionVisibility(regionId);
      }
    });

    // Apply smart selection logic: Customer Types AND Regions with proper tracking
    applySmartSelection(selectedCustomerGroups, updatedValues);

    // After applying selection, focus map on addresses from selected regions
    const selectedRegionIdsFiltered = updatedValues.filter(
      (v) => v !== "__ALL_REGIONS__",
    );

    if (selectedRegionIdsFiltered.length > 0) {
      const addressesInSelectedRegions = addresses.filter(
        (address) =>
          address.region &&
          selectedRegionIdsFiltered.includes(address.region.id),
      );

      if (addressesInSelectedRegions.length > 0) {
        // Use setTimeout to ensure this happens after the selection is applied
        setTimeout(() => {
          setMapCentering(undefined, addressesInSelectedRegions);
        }, 0);
      }
    }
  };

  // Format customer group display value
  const getCustomerGroupDisplayValue = () => {
    if (selectedCustomerGroups.length === 0) return undefined;

    // If "all" is selected, just show "All Customers" regardless of other selections
    if (selectedCustomerGroups.includes("all")) {
      return "All Customers";
    }

    const labels = selectedCustomerGroups.map((value) => {
      const option = customerGroupOptions.find((opt) => opt.value === value);
      return option?.label.trim() || value;
    });

    if (labels.length === 1) return labels[0];
    if (labels.length === 2) return `${labels[0]}, ${labels[1]}`;
    return `${labels[0]} +${labels.length - 1}`;
  };

  // Apply smart selection logic with proper tracking and removal
  const applySmartSelection = (
    customerGroups: string[],
    regionIds: string[],
  ) => {
    // Always preserve existing recipients from API (these should NEVER be removed)
    const apiRecipients = selectedAddresses.filter((addr) =>
      addr.id.startsWith("recipient-"),
    );

    // Quick Select should clear all existing selections except API recipients
    // to avoid conflicting recipient states
    const preservedSelections = [...apiRecipients];

    // If no quick selections, just keep API recipients (clear all other selections)
    if (customerGroups.length === 0 && regionIds.length === 0) {
      setAddressesAddedByQuickSelect({
        customerGroups: new Set(),
        regions: new Set(),
      });
      setSelectedAddress(preservedSelections, false, false);
      return;
    }

    let quickSelectAddresses: MapMarker[] = [];
    const newCustomerGroupAddresses = new Set<string>();
    const newRegionAddresses = new Set<string>();

    // Case 1: Only customer groups selected (no region filter)
    if (customerGroups.length > 0 && regionIds.length === 0) {
      quickSelectAddresses = addresses.filter((address) => {
        const matches = matchesCustomerGroups(address, customerGroups);
        if (matches && !address.id.startsWith("recipient-")) {
          // Don't track API recipients as quick select addresses
          newCustomerGroupAddresses.add(address.id);
        }
        return matches;
      });
    }
    // Case 2: Only regions selected (no customer filter)
    else if (regionIds.length > 0 && customerGroups.length === 0) {
      quickSelectAddresses = addresses.filter((address) => {
        const matches = matchesRegions(address, regionIds);
        if (matches && !address.id.startsWith("recipient-")) {
          // Don't track API recipients as quick select addresses
          newRegionAddresses.add(address.id);
        }
        return matches;
      });
    }
    // Case 3: Both customer groups AND regions selected (intersection)
    else if (customerGroups.length > 0 && regionIds.length > 0) {
      quickSelectAddresses = addresses.filter((address) => {
        const matchesCustomer = matchesCustomerGroups(address, customerGroups);
        const matchesRegion = matchesRegions(address, regionIds);
        const matches = matchesCustomer && matchesRegion;

        if (matches && !address.id.startsWith("recipient-")) {
          // Don't track API recipients as quick select addresses
          // These addresses match both criteria, so they belong to both sets
          newCustomerGroupAddresses.add(address.id);
          newRegionAddresses.add(address.id);
        }
        return matches;
      });
    }

    // Combine preserved selections with quick select results
    const finalSelection = [...preservedSelections];

    // Add quick select results (avoid duplicates)
    quickSelectAddresses.forEach((addr) => {
      if (!finalSelection.some((existing) => existing.id === addr.id)) {
        finalSelection.push({ ...addr, selected: true });
      }
    });

    // Update tracking
    setAddressesAddedByQuickSelect({
      customerGroups: newCustomerGroupAddresses,
      regions: newRegionAddresses,
    });

    setSelectedAddress(finalSelection, false, false, true); // isQuickSelect = true
  };

  // Helper function to check if address matches customer groups
  const matchesCustomerGroups = (
    address: MapMarker,
    customerGroups: string[],
  ): boolean => {
    if (customerGroups.length === 0) return true;
    if (customerGroups.includes("all")) return true;

    return (
      (customerGroups.includes("flatRate") && !!address.isFlatRate) ||
      (customerGroups.includes("onDemand") && !!address.isOnDemand) ||
      (customerGroups.includes("snowReporters") && !!address.isSnowReporter)
    );
  };

  // Helper function to check if address matches regions
  const matchesRegions = (address: MapMarker, regionIds: string[]): boolean => {
    if (regionIds.length === 0) return true;
    if (regionIds.includes("__ALL_REGIONS__")) {
      return !!address.region;
    }

    const actualRegionIds = regionIds.filter((id) => id !== "__ALL_REGIONS__");
    return !!(address.region && actualRegionIds.includes(address.region.id));
  };

  // Check if Quick Select dropdowns should be reset when addresses are removed individually
  const checkAndResetQuickSelectDropdowns = () => {
    // We need to check the remaining addresses after the removal
    // Use setTimeout to ensure this runs after the address is actually removed from selectedAddresses
    setTimeout(() => {
      const currentSelectedAddresses =
        useNotificationRecipientsStore.getState().selectedAddresses;

      // Check if we need to reset customer group selection
      if (selectedCustomerGroups.length > 0) {
        // Check if any remaining selected addresses still match the current customer group selection
        const stillHasMatchingCustomerGroups = currentSelectedAddresses.some(
          (addr) => matchesCustomerGroups(addr, selectedCustomerGroups),
        );

        // If no remaining addresses match the customer group selection, reset it
        if (!stillHasMatchingCustomerGroups) {
          setSelectedCustomerGroups([]);
        }
      }

      // Check if we need to reset region selection (more granular approach)
      if (selectedRegionIds.length > 0) {
        // Get all region IDs that still have selected addresses
        const regionsWithSelectedAddresses = new Set<string>();
        currentSelectedAddresses.forEach((addr) => {
          if (addr.region) {
            regionsWithSelectedAddresses.add(addr.region.id);
          }
        });

        // Filter out regions that no longer have any selected addresses
        const updatedRegionIds = selectedRegionIds.filter((regionId) => {
          // Keep the "__ALL_REGIONS__" option only if all regions still have addresses
          if (regionId === "__ALL_REGIONS__") {
            // Only keep "All Regions" if we still have the same set of regions as before
            const allRegionIds = availableRegions.map((r) => r.id);
            return allRegionIds.every((id) =>
              regionsWithSelectedAddresses.has(id),
            );
          }
          // Keep individual region IDs only if they still have selected addresses
          return regionsWithSelectedAddresses.has(regionId);
        });

        // Update the region selection only if it changed
        if (updatedRegionIds.length !== selectedRegionIds.length) {
          setSelectedRegionIds(updatedRegionIds);
        }
      }
    }, 0);
  };

  // Check if any selected recipients are hidden by current filters
  const hiddenSelectedRecipients = useMemo(() => {
    return selectedAddresses.filter((address) => {
      // 1. Check region filter - address in region + region toggle OFF -> HIDDEN
      if (address.region && !visibleRegions.has(address.region.id)) {
        return true;
      }

      // 2. Check customer type filters - if all customer toggles are OFF -> HIDDEN
      if (!showFlatRateOnly && !showOnDemandOnly && !showSnowReporters) {
        return true;
      }

      // 3. If all customer type filters are ON, no address should be hidden by customer type
      const allCustomerFiltersOn =
        showFlatRateOnly && showOnDemandOnly && showSnowReporters;
      if (allCustomerFiltersOn) {
        return false; // Not hidden when all filters are enabled
      }

      // 4. Check if address matches any enabled customer type filter
      const matchesFlatRate = showFlatRateOnly && address.isFlatRate;
      const matchesOnDemand = showOnDemandOnly && address.isOnDemand;
      const matchesSnowReporter = showSnowReporters && address.isSnowReporter;

      // If none of the enabled toggles match this address, it's hidden
      if (!matchesFlatRate && !matchesOnDemand && !matchesSnowReporter) {
        return true;
      }

      return false;
    });
  }, [
    selectedAddresses,
    visibleRegions,
    showFlatRateOnly,
    showOnDemandOnly,
    showSnowReporters,
  ]);

  // Handle reset - clear both selected addresses and dropdown states
  const handleReset = () => {
    // Clear selected addresses
    onResetSelection();

    // Clear dropdown states
    setSelectedCustomerGroups([]);
    setSelectedRegionIds([]);

    // Clear tracking
    setAddressesAddedByQuickSelect({
      customerGroups: new Set(),
      regions: new Set(),
    });

    // Clear map centering when resetting to prevent unwanted zooming
    clearMapCentering();
  };

  // Format region display value
  const getRegionDisplayValue = () => {
    if (selectedRegionIds.length === 0) return undefined;

    if (selectedRegionIds.includes("__ALL_REGIONS__")) {
      return "All Regions";
    }

    const labels = selectedRegionIds.map((regionId) => {
      const region = availableRegions.find((r) => r.id === regionId);
      return region?.name || regionId;
    });

    if (labels.length === 1) return labels[0];
    if (labels.length === 2) return `${labels[0]}, ${labels[1]}`;
    return `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
  };

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.instructionText}>
        Select customers from the map or use quick selection to replace current
        selections with customers from different groups and regions.
      </Typography>

      <Box sx={styles.quickSelectSection}>
        <Typography fontWeight="600" fontSize="14px" marginBottom="12px">
          Quick select
        </Typography>

        <Box sx={styles.selectGroup}>
          <FormControl fullWidth sx={{ marginBottom: "12px" }}>
            <InputLabel size="small">Select customer group</InputLabel>
            <Select
              multiple
              value={selectedCustomerGroups}
              onChange={(e) =>
                handleCustomerGroupChange(e.target.value as string[])
              }
              input={
                <OutlinedInput label="Select customer group" size="small" />
              }
              renderValue={() =>
                getCustomerGroupDisplayValue() || "Select customer group"
              }
              size="small"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              data-testid="customer-group-select"
            >
              {customerGroupOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    checked={selectedCustomerGroups.includes(option.value)}
                    size="small"
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel size="small">Select regions</InputLabel>
            <Select
              multiple
              value={selectedRegionIds}
              onChange={(e) => handleRegionChange(e.target.value as string[])}
              input={<OutlinedInput label="Select regions" size="small" />}
              renderValue={() => getRegionDisplayValue() || "Select regions"}
              size="small"
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 200,
                  },
                },
              }}
              data-testid="region-select"
            >
              {regionOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox
                    checked={selectedRegionIds.includes(option.value)}
                    size="small"
                  />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={styles.selectedHeader}>
        <Typography fontWeight="600" fontSize="14px">
          {selectedAddresses.length} selected
        </Typography>
        <Button
          onClick={handleReset}
          size="small"
          disabled={selectedAddresses.length === 0}
        >
          Reset
        </Button>
      </Box>

      {invalidCustomers > 0 && (
        <Alert variant="standard" color="warning" sx={{ marginBottom: "12px" }}>
          {invalidCustomers} customer
          {invalidCustomers !== 1 ? "s have" : " has"} issues with their
          account. Notifications will not be sent to these customers.
        </Alert>
      )}

      {hiddenSelectedRecipients.length > 0 && (
        <Alert variant="standard" color="error" sx={{ marginBottom: "12px" }}>
          Some recipients you've selected aren't visible on the map because of
          the active filters.
        </Alert>
      )}

      <Box sx={styles.selectedSection}>
        {selectedAddresses.length > 0 ? (
          <Box sx={styles.addressesList}>
            {selectedAddresses.map((address) => (
              <AddressBoxComponent
                {...{ ...address, region: undefined, selected: true }}
                key={address.id}
                onHandleChange={(id) => handleToggleAddress(id)}
                view="notification-recipients"
              />
            ))}
          </Box>
        ) : (
          <Box sx={styles.emptyState}>
            <Typography color="textSecondary" textAlign="center">
              No addresses selected
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeSxStyles((theme) => ({
  container: {
    width: "400px",
    height: "calc(100% - 32px)",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "12px",
    display: "flex",
    flexDirection: "column",
    padding: "16px",
    margin: "16px",
    marginLeft: "16px",
  },
  header: {
    marginBottom: "16px",
    paddingBottom: "8px",
  },
  instructionText: {
    fontSize: "14px",
    color: theme.palette.text.secondary,
    marginBottom: "20px",
    lineHeight: "1.4",
  },
  quickSelectSection: {
    marginBottom: "20px",
    paddingBottom: "16px",
  },
  selectGroup: {
    display: "flex",
    flexDirection: "column",
  },
  selectedHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    paddingBottom: "8px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  selectedSection: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  addressesList: {
    flex: 1,
    overflowY: "auto",
    maxHeight: "calc(100vh - 400px)",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  emptyState: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 16px",
  },
}));

export default NotificationRecipientsSidebar;
